import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../context/darkModeContext";
import { useDepartment } from "../stores/departmentStore";
import useAuth from "../stores/authStore";
import { auth } from "../helper/firebaseClient";
import { api } from "../utils/axios-instance";
import secureLocalStorage from "react-secure-storage";
import toast from "react-hot-toast";
import { gapi } from "gapi-script";
import TwoKeyLogo from "../assets/TwoKey.jpeg";
import { useStorageStore } from "../store/storageStore";
import { isStarterPlan } from "../utils/planAccess";
import { subscriptionBlocksAccess } from "./ProtectedRoute";
import "./Sidebar.css";

/* ── Inline SVG icons ───────────────────────────────────────────────────── */
const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" />
  </svg>
);
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const VendorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1.5l6 3v7l-6 3-6-3v-7l6-3z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M2 4.5L8 7.5l6-3"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M8 7.5v7"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);
const TasksIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect
      x="3"
      y="2.5"
      width="10"
      height="12"
      rx="1.6"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M6 1.5h4a1 1 0 011 1v1H5v-1a1 1 0 011-1z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M5.5 8.5l1.5 1.5L11 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <polyline
      points="11,11 14,8 11,5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="14"
      y1="8"
      x2="6"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
const StorageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect
      x="1"
      y="4"
      width="14"
      height="4"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect
      x="1"
      y="10"
      width="14"
      height="4"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <polyline
      points="2,4 14,4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <rect
      x="3"
      y="4"
      width="10"
      height="10"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);
const CollapseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <polyline
      points="8,2 4,6 8,10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const ExpandIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <polyline
      points="4,2 8,6 4,10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <line
      x1="1"
      y1="1"
      x2="13"
      y2="13"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <line
      x1="13"
      y1="1"
      x2="1"
      y2="13"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

/* ── NavItem ─────────────────────────────────────────────────────────────── */
const NavItem = ({ icon, label, active, onClick }) => (
  <div className={`nav-item ${active ? "active" : ""}`} onClick={onClick}>
    <span className="nav-icon">{icon}</span>
    <span className="nav-label">{label}</span>
  </div>
);

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function barColor(pct) {
  if (pct >= 85) return "#ef4444";
  if (pct >= 80) return "#f97316";
  return "#5E5ADB";
}

function fmtBytes(b) {
  if (!b || b <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i++;
  }
  return `${b.toFixed(1)} ${units[i]}`;
}

/* ── Storage Panel (shown on click) ─────────────────────────────────────── */
function StoragePanel({ orgData, userData, onClose, darkMode }) {
  const panelRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function onDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const bg = darkMode ? "#1f2937" : "#ffffff";
  const border = darkMode ? "#374151" : "#e8eaf0";
  const title = darkMode ? "#f9fafb" : "#111827";
  const sub = darkMode ? "#9ca3af" : "#6b7280";
  const track = darkMode ? "#374151" : "#f3f4f6";

  return (
    <div className="sp-overlay">
      <div
        className="sp-panel"
        ref={panelRef}
        style={{ background: bg, border: `1px solid ${border}` }}
      >
        {/* Header */}
        <div className="sp-header">
          <div>
            <div className="sp-title" style={{ color: title }}>
              Storage Usage
            </div>
            <div className="sp-sub" style={{ color: sub }}>
              Your usage · Org total
            </div>
          </div>
          <button className="sp-close" onClick={onClose} style={{ color: sub }}>
            <CloseIcon />
          </button>
        </div>

        {/* ── MY STORAGE ─────────────────────────────────────── */}
        <div className="sp-section">
          <div className="sp-section-label" style={{ color: sub }}>
            MY STORAGE
          </div>

          <div className="sp-stat-row">
            <span className="sp-stat-used" style={{ color: title }}>
              {fmtBytes(userData.usedBytes)}
            </span>
            <span className="sp-stat-of" style={{ color: sub }}>
              of
            </span>
            <span className="sp-stat-quota" style={{ color: title }}>
              {fmtBytes(userData.quotaBytes)}
            </span>
            <span
              className="sp-stat-pct"
              style={{ color: barColor(userData.percent) }}
            >
              {userData.percent}%
            </span>
          </div>

          <div className="sp-bar-track" style={{ background: track }}>
            <div
              className="sp-bar-fill"
              style={{
                width: `${Math.min(userData.percent, 100)}%`,
                background: barColor(userData.percent),
              }}
            />
          </div>

          {userData.percent >= 98 && (
            <div className="sp-warn">
              🚫 Uploads blocked — quota nearly full
            </div>
          )}
          {userData.percent >= 85 && userData.percent < 98 && (
            <div className="sp-warn">
              ⚠ Critical — almost at your storage limit
            </div>
          )}
          {userData.percent >= 80 && userData.percent < 85 && (
            <div className="sp-warn" style={{ color: "#f97316" }}>
              ⚠ Storage high — consider freeing up space
            </div>
          )}

          {/* Per-seat note */}
          <div className="sp-note" style={{ color: sub }}>
            Your personal allocation from the{" "}
            <strong style={{ color: title }}>{orgData.plan}</strong> plan ·{" "}
            {orgData.seats} seat{orgData.seats !== 1 ? "s" : ""} total
          </div>
        </div>

        {/* Divider */}
        <div className="sp-divider" style={{ background: border }} />

        {/* ── ORG STORAGE ────────────────────────────────────── */}
        <div className="sp-section">
          <div className="sp-section-label" style={{ color: sub }}>
            ORG STORAGE
          </div>

          <div className="sp-stat-row">
            <span className="sp-stat-used" style={{ color: title }}>
              {fmtBytes(orgData.usedBytes)}
            </span>
            <span className="sp-stat-of" style={{ color: sub }}>
              of
            </span>
            <span className="sp-stat-quota" style={{ color: title }}>
              {fmtBytes(orgData.quotaBytes)}
            </span>
            <span
              className="sp-stat-pct"
              style={{ color: barColor(orgData.percent) }}
            >
              {orgData.percent}%
            </span>
          </div>

          <div className="sp-bar-track" style={{ background: track }}>
            <div
              className="sp-bar-fill"
              style={{
                width: `${Math.min(orgData.percent, 100)}%`,
                background: barColor(orgData.percent),
              }}
            />
          </div>

          {orgData.isOverQuota && (
            <div className="sp-warn">⚠ Organisation quota exceeded</div>
          )}

          <div className="sp-note" style={{ color: sub }}>
            Shared across all {orgData.seats} member
            {orgData.seats !== 1 ? "s" : ""} ·{" "}
            {fmtBytes(orgData.quotaBytes - orgData.usedBytes)} remaining
          </div>
        </div>

        {/* ── PLAN BADGE ─────────────────────────────────────── */}
        <div className="sp-plan-row">
          <span className="sp-plan-badge">
            {orgData.plan?.toUpperCase() || "STARTER"}
          </span>
          <span style={{ color: sub, fontSize: 11 }}>
            {orgData.seats} seat{orgData.seats !== 1 ? "s" : ""} ·{" "}
            {fmtBytes(orgData.quotaBytes / Math.max(orgData.seats, 1))}/seat
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── SideBar ─────────────────────────────────────────────────────────────── */
/* ── Hamburger SVG icon ─────────────────────────────────────────────────── */
const HamburgerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <line
      x1="2"
      y1="4.5"
      x2="16"
      y2="4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <line
      x1="2"
      y1="9"
      x2="16"
      y2="9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <line
      x1="2"
      y1="13.5"
      x2="16"
      y2="13.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

function SideBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { profileData, setProfileData, token, setToken } = useAuth();
  const { departments } = useDepartment();
  const [panelOpen, setPanelOpen] = useState(false);
  const [vendorRooms, setVendorRooms] = useState([]);
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  // ── Responsive collapse state ────────────────────────────────────────────
  // On mobile/tablet (<1024px) sidebar starts collapsed; on desktop it starts open.
  const isMobileOrTablet = () => window.innerWidth < 1024;
  const [collapsed, setCollapsed] = useState(() => isMobileOrTablet());

  // Re-evaluate on resize (e.g. rotating device, resizing browser)
  useEffect(() => {
    const handleResize = () => {
      // Only auto-open when crossing to desktop; don't force-close on mobile if user opened it
      if (!isMobileOrTablet()) {
        setCollapsed(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on route change on mobile (UX: auto-dismiss after navigation)
  useEffect(() => {
    if (isMobileOrTablet()) {
      setCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Poll vendor rooms for the sidebar dropdown ──────────────────────────
  useEffect(() => {
    if (!token) return;
    const fetchVendorRooms = async () => {
      try {
        const response = await api.get("/vendors/rooms/");
        setVendorRooms(response.data?.deliveryRooms || []);
      } catch (err) {
        console.error("Failed to fetch sidebar vendor channels:", err);
      }
    };
    fetchVendorRooms();
    const intervalId = setInterval(fetchVendorRooms, 15000);
    return () => clearInterval(intervalId);
  }, [token]);

  // ── Org-level storage from store ────────────────────────────────────────
  const orgPercent = useStorageStore((s) => s.percent);
  const orgUsedBytes = useStorageStore((s) => s.usedBytes);
  const orgQuotaBytes = useStorageStore((s) => s.quotaBytes);
  const orgUsedFormatted = useStorageStore((s) => s.usedFormatted);
  const orgQuotaFormatted = useStorageStore((s) => s.quotaFormatted);
  const orgIsOverQuota = useStorageStore((s) => s.isOverQuota);
  const orgPlan = useStorageStore((s) => s.plan);
  const orgSeats = useStorageStore((s) => s.seats);
  const storageLoading = useStorageStore((s) => s.loading);

  // ── User-level storage — REAL bytes this user uploaded (from backend) ───
  // No more division estimate. The backend reads organizations/{org}/users/{uid}
  // .storage.used_bytes which is incremented atomically on every upload.
  const userUsedBytes = useStorageStore((s) => s.userUsedBytes);
  const userQuotaBytes = useStorageStore((s) => s.userQuotaBytes);
  const userUsedFormatted = useStorageStore((s) => s.userUsedFormatted);
  const userQuotaFormatted = useStorageStore((s) => s.userQuotaFormatted);
  const userPercent = useStorageStore((s) => s.userPercent);
  const userIsOverQuota = useStorageStore((s) => s.userIsOverQuota);

  // Stable action reference — never triggers useEffect re-runs
  const fetchStorageUsage = useStorageStore.getState().fetchStorageUsage;

  useEffect(() => {
    if (!token) return;
    fetchStorageUsage();
    const id = setInterval(fetchStorageUsage, 10_000);
    return () => clearInterval(id);
  }, [token]);

  // ── Pill values — show user's real personal storage in the default pill ──
  // All values come directly from the store which reads them from the backend.
  // The backend reads organizations/{org_id}/users/{uid}.storage.used_bytes —
  // the actual sum of files this specific user has uploaded. No more estimates.
  const pillPct = userPercent;
  const pillUsed = userUsedFormatted;
  const pillQuota = userQuotaFormatted;
  const pillOver = userPercent >= 80; // warn from 80% onwards
  const pillBlocked = userPercent >= 98; // hard block at 98%
  const pillColor = barColor(userPercent);

  // Tiered warning level for the pill sub-line
  const pillWarnLevel =
    userPercent >= 98
      ? "blocked"
      : userPercent >= 85
        ? "critical"
        : userPercent >= 80
          ? "warning"
          : "ok";

  // ── Visible departments ──────────────────────────────────────────────────
  const visibleDepartments = React.useMemo(() => {
    if (!profileData) return [];
    if (
      profileData.role_priv === "org_admin" ||
      profileData.role_priv === "super_admin"
    ) {
      return departments || [];
    }
    const ids = profileData.dept_ids || [];
    const names = profileData.dept_names || [];
    if (ids.length)
      return (departments || []).filter((d) => ids.includes(d.id));
    if (names.length)
      return (departments || []).filter((d) => names.includes(d.name));
    return [];
  }, [profileData, departments]);

  /* ── Hide on public routes ── */
  const hideSideBar =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/onboard" ||
    location.pathname === "/signup" ||
    location.pathname === "/create-organization" ||
    location.pathname === "/change-password" ||
    location.pathname === "/onboarding" ||
    location.pathname === "/waiting-lobby" ||
    location.pathname === "/contact-us" ||
    location.pathname === "/chat" ||
    location.pathname === "/privacy-policy" ||
    // expired / unpaid: no navigation at all, only /checkout is reachable
    subscriptionBlocksAccess(profileData) ||
    !auth.currentUser;

  if (hideSideBar || !token) return null;

  /* ── Logout ── */
  async function handleLogout() {
    try {
      const idToken = await auth.currentUser.getIdToken();
      await api.put(`/auth/logout/`, { idToken, is_active: false });
      secureLocalStorage.removeItem("profileData");
      secureLocalStorage.removeItem("token");
      try {
        const ai = gapi.auth2.getAuthInstance();
        await ai.signOut();
      } catch (_) {}
      localStorage.removeItem("google_token");
      localStorage.removeItem("google_refresh_token");
      await auth.signOut();
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
    navigate("/");
    setProfileData(null);
    setToken(null);
    secureLocalStorage.clear();
  }

  const isActive = (path) => location.pathname === path;
  const isDeptActive = (name) =>
    decodeURIComponent(location.pathname.split("/").pop() || "") === name;

  const userTag = profileData?.username
    ? `@${profileData.username}`
    : "#TWK523";
  const userAvatar = profileData?.profilePictureUrl;

  // Data objects passed to the panel
  const orgData = {
    usedBytes: orgUsedBytes,
    quotaBytes: orgQuotaBytes,
    percent: orgPercent,
    isOverQuota: orgIsOverQuota,
    plan: orgPlan,
    seats: orgSeats,
  };
  const userData = {
    usedBytes: userUsedBytes,
    quotaBytes: userQuotaBytes,
    percent: userPercent,
    isOverQuota: userIsOverQuota,
  };

  const isMobile = () => window.innerWidth < 768;

  return (
    <>
      {/* ── Hamburger button (mobile + tablet) ── */}
      {collapsed && (
        <button
          className="sidebar-hamburger"
          onClick={() => setCollapsed(false)}
          title="Open menu"
          aria-label="Open navigation menu"
          style={
            darkMode
              ? {
                  background: "#1f2937",
                  borderColor: "#374151",
                  color: "#d1d5db",
                }
              : {}
          }
        >
          <HamburgerIcon />
        </button>
      )}

      {/* ── Desktop collapsed open-tab (only shown on desktop ≥1024px) ── */}
      {collapsed && (
        <button
          className="sidebar-expand-tab"
          onClick={() => setCollapsed(false)}
          style={{
            position: "fixed",
            top: 5,
            left: 0,
            zIndex: 200,
            width: "20px",
            height: "48px",
            background: darkMode ? "#1f2937" : "#ffffff",
            border: "1px solid #e8eaf0",
            borderLeft: "none",
            borderRadius: "0 8px 8px 0",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            boxShadow: "2px 0 8px rgba(0,0,0,0.07)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#4f46e5")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          title="Open sidebar"
        >
          <ExpandIcon />
        </button>
      )}

      {/* ── Mobile backdrop — closes sidebar on outside tap ── */}
      {!collapsed && (
        <div
          className={`sidebar-backdrop${collapsed ? " sidebar-backdrop--hidden" : ""}`}
          onClick={() => setCollapsed(true)}
          aria-hidden="true"
        />
      )}

      {/* ── Storage detail panel (portal-like overlay) ── */}
      {panelOpen && (
        <StoragePanel
          orgData={orgData}
          userData={userData}
          onClose={() => setPanelOpen(false)}
          darkMode={darkMode}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`sidebar${collapsed ? " sidebar-collapsed" : ""}`}
        style={
          darkMode ? { background: "#111827", borderColor: "#1f2937" } : {}
        }
      >
        {/* Logo row */}
        <div className="sidebar-logo">
          <button
            className="logo-home-btn"
            onClick={() => navigate("/dashboard")}
            title="Dashboard"
          >
            <img src={TwoKeyLogo} alt="TwoKey" className="sidebar-brand-logo" />
          </button>
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed(true)}
            title="Collapse"
          >
            <CollapseIcon />
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            <span
              className="section-label"
              style={darkMode ? { color: "#6b7280" } : {}}
            >
              Overview
            </span>
            <NavItem
              icon={<GridIcon />}
              label="Dashboard"
              active={isActive("/dashboard")}
              onClick={() => navigate("/dashboard")}
            />
          </div>

          {profileData?.role_priv === "org_admin" &&
            !isStarterPlan(orgPlan) && (
              <div className="nav-section">
                <span
                  className="section-label"
                  style={darkMode ? { color: "#fff" } : {}}
                >
                  Organization
                </span>
                <NavItem
                  icon={<UserIcon />}
                  label="User Manage..."
                  active={isActive("/user-management")}
                  onClick={() => navigate("/user-management")}
                />
              </div>
            )}

          <div className="nav-section">
            <span
              className="section-label"
              style={darkMode ? { color: "#fff" } : {}}
            >
              Productivity
            </span>
            <NavItem
              icon={<TasksIcon />}
              label="Tasks & Tickets"
              active={isActive("/tasks-and-tickets")}
              onClick={() => navigate("/tasks-and-tickets")}
            />

            <div
              className={`nav-item ${location.pathname === "/vendors" ? "active" : ""}`}
              onClick={() => navigate("/vendors")}
              style={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <span className="nav-icon">
                <VendorIcon />
              </span>
              <span
                className="nav-label"
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                Vendor
                <span
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents navigation trigger
                    setVendorDropdownOpen(!vendorDropdownOpen);
                  }}
                >
                  {vendorDropdownOpen ? <CollapseIcon /> : <ExpandIcon />}
                </span>
              </span>
            </div>

            {vendorDropdownOpen &&
              vendorRooms.map((room) => {
                const active = location.pathname === `/vendors/room/${room.id}`;
                const fallbackColor = "#10b981"; // Emerald green for vendor entries
                return (
                  <div
                    key={room.id}
                    className={`nav-item vendor-room-item ${active ? "active" : ""}`}
                    style={{
                      paddingLeft: "24px",
                      ...(active
                        ? {
                            background: fallbackColor + "22",
                            color: fallbackColor,
                          }
                        : {}),
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/vendors/room/${room.id}`);
                    }}
                  >
                    {/* <span className="nav-icon">
                      <span
                        style={{
                          display: "inline-block",
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: fallbackColor,
                          flexShrink: 0,
                        }}
                      />
                    </span> */}
                    <span
                      className="nav-label"
                      style={
                        darkMode
                          ? { color: active ? fallbackColor : "#d1d5db" }
                          : {}
                      }
                    >
                      {room.clientName}
                    </span>
                  </div>
                );
              })}
          </div>

          <div className="nav-section">
            <span
              className="section-label"
              style={darkMode ? { color: "#fff" } : {}}
            >
              Department
            </span>
            {visibleDepartments.map((dept) => {
              const dot = dept.metadata?.bg || "#6366f1";
              const active = isDeptActive(dept.name);
              return (
                <div
                  key={dept.id}
                  className={`nav-item ${active ? "active" : ""}`}
                  style={active ? { background: dot + "22", color: dot } : {}}
                  onClick={() =>
                    navigate(`/department/${encodeURIComponent(dept.name)}`)
                  }
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = dot + "14";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "";
                  }}
                >
                  <span className="nav-icon">
                    <span
                      style={{
                        display: "inline-block",
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: dot,
                        flexShrink: 0,
                      }}
                    />
                  </span>
                  <span
                    className="nav-label"
                    style={darkMode ? { color: active ? dot : "#d1d5db" } : {}}
                  >
                    {dept.name.replace("_", " ")}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="nav-section">
            <span
              className="section-label"
              style={darkMode ? { color: "#fff" } : {}}
            >
              Settings
            </span>
            <NavItem
              icon={<SettingsIcon />}
              label="Settings"
              active={isActive("/settings")}
              onClick={() => navigate("/settings")}
            />
            <NavItem
              icon={<LogoutIcon />}
              label="Logout"
              active={false}
              onClick={handleLogout}
            />
          </div>

          <div className="nav-section">
            <span
              className="section-label"
              style={darkMode ? { color: "#fff" } : {}}
            >
              Recycle Bin
            </span>
            <NavItem
              icon={<TrashIcon />}
              label="Recycle Bin"
              active={isActive("/recyclebin")}
              onClick={() => navigate("/recyclebin")}
            />
          </div>
        </nav>

        {/* ── Footer ── */}
        <div
          className="sidebar-footer"
          style={darkMode ? { borderColor: "#1f2937" } : {}}
        >
          {/* ── Storage pill (clickable, shows USER storage by default) ── */}
          <div
            className="storage-block storage-block--clickable"
            onClick={() => setPanelOpen(true)}
            title="Click to see storage details"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setPanelOpen(true)}
          >
            {/* Row: icon · label · percent */}
            <div className="storage-row">
              <span
                className="storage-icon"
                style={{
                  color: pillWarnLevel !== "ok" ? pillColor : undefined,
                }}
              >
                <StorageIcon />
              </span>
              <span
                className="storage-label"
                style={darkMode ? { color: "#d1d5db" } : {}}
              >
                Storage
              </span>
              <span
                className="storage-pct"
                style={{
                  color:
                    pillWarnLevel !== "ok"
                      ? pillColor
                      : darkMode
                        ? "#f9fafb"
                        : undefined,
                  fontWeight: pillWarnLevel !== "ok" ? 600 : undefined,
                }}
              >
                {storageLoading ? "…" : `${pillPct}%`}
              </span>
            </div>

            {/* Progress bar — user's usage */}
            <div className="storage-bar">
              <div
                className="storage-fill"
                style={{
                  width: storageLoading ? "0%" : `${Math.min(pillPct, 100)}%`,
                  backgroundColor: pillColor,
                  transition: "width 0.5s ease, background-color 0.3s ease",
                }}
              />
            </div>

            {/* Sub-line: used / quota — tiered messages */}
            {!storageLoading && (
              <div
                className="storage-sub"
                style={{
                  color:
                    pillWarnLevel === "blocked" || pillWarnLevel === "critical"
                      ? "#ef4444"
                      : pillWarnLevel === "warning"
                        ? "#f97316"
                        : darkMode
                          ? "#6b7280"
                          : "#9ca3af",
                }}
              >
                {pillWarnLevel === "blocked"
                  ? `🚫 Uploads blocked · ${pillUsed} used`
                  : pillWarnLevel === "critical"
                    ? `⚠ Almost full · ${pillUsed} of ${pillQuota}`
                    : pillWarnLevel === "warning"
                      ? `⚠ Storage high · ${pillUsed} of ${pillQuota}`
                      : `${pillUsed} of ${pillQuota}`}
              </div>
            )}
          </div>
          {/* ─────────────────────────────────────────────────────────────── */}

          {/* User row */}
          <div
            className="user-row"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/profile")}
            title="View profile"
          >
            {userAvatar ? (
              <img
                src={userAvatar}
                alt="avatar"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div className="user-avatar">
                {profileData?.username?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <span
              className="user-tag"
              style={darkMode ? { color: "#9ca3af" } : {}}
            >
              {userTag}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}

export default SideBar;
